/**
 * The small, shared coordination schema used by the upload engine.
 *
 * This schema deliberately contains coordination facts only. Application upload
 * rows and chunk stores remain app-owned and can be registered as additional
 * object stores by an adapter; the library never deletes or migrates them.
 */

export const DURABLE_UPLOAD_DATABASE_NAME = "liveswitch-upload-coordination";
export const DURABLE_UPLOAD_DATABASE_VERSION = 1;

export const DURABLE_UPLOAD_STORE_NAMES = {
	leases: "leases",
	work: "work",
	poison: "poison"
} as const;

export type DurableUploadStoreName = (typeof DURABLE_UPLOAD_STORE_NAMES)[keyof typeof DURABLE_UPLOAD_STORE_NAMES] | (string & {});

export type DurableLeaseKind = "scheduler" | "writer";

export interface DurableLeaseScope {
	readonly kind: DurableLeaseKind;
	readonly originKey: string;
	readonly identity?: DurableItemIdentity;
}

export interface DurableItemIdentity {
	/** Stable logical upload/session identity. */
	readonly itemId: string;
	/** Stable take identity for recording uploads; omitted for non-take uploads. */
	readonly takeId?: string;
}

export interface DurableLeaseRecord {
	readonly key: string;
	readonly kind: DurableLeaseKind;
	readonly originKey: string;
	readonly itemId?: string;
	readonly takeId?: string;
	readonly ownerId: string;
	readonly epoch: number;
	readonly acquiredAt: number;
	readonly expiresAt: number;
	readonly workGeneration?: number;
	/**
	 * LSP-23117: the Web Lock the owner holds for as long as it is alive. Present only when the owner had
	 * the lock when it wrote the row; a row without it (older builds, no Web Locks) is released by TTL only.
	 */
	readonly livenessLock?: string;
}

export interface DurableLeaseToken extends DurableLeaseScope {
	readonly ownerId: string;
	readonly epoch: number;
	readonly acquiredAt: number;
	readonly expiresAt: number;
	readonly workGeneration?: number;
}

export interface DurableWorkRecord {
	readonly originKey: string;
	readonly workGeneration: number;
	readonly pendingCount: number;
	readonly hasDueWork: boolean;
}

export interface DurablePoisonRecord {
	readonly key: string;
	readonly itemId: string;
	readonly takeId?: string;
	readonly failureCount: number;
	readonly ceiling: number;
	readonly lastFailureAt: number;
	readonly lastReason: string;
}

export function durableIdentityKey(identity: DurableItemIdentity): string {
	if (!identity || typeof identity.itemId !== "string" || !identity.itemId || identity.itemId.trim() !== identity.itemId) {
		throw new Error("Durable upload item identity must be a non-empty, trimmed string");
	}
	if (
		identity.takeId !== undefined &&
		(typeof identity.takeId !== "string" || !identity.takeId || identity.takeId.trim() !== identity.takeId)
	) {
		throw new Error("Durable upload take identity must be a non-empty, trimmed string when present");
	}
	return JSON.stringify([identity.itemId, identity.takeId ?? null]);
}

export function schedulerLeaseKey(originKey: string): string {
	if (typeof originKey !== "string" || !originKey || originKey.trim() !== originKey) {
		throw new Error("Durable upload origin key is invalid");
	}
	return `scheduler:${originKey}`;
}

export function writerLeaseKey(originKey: string, identity: DurableItemIdentity): string {
	if (!originKey || originKey.trim() !== originKey) throw new Error("Durable upload origin key is invalid");
	return `writer:${originKey}:${durableIdentityKey(identity)}`;
}

export function poisonRecordKey(originKey: string, identity: DurableItemIdentity): string {
	if (!originKey || originKey.trim() !== originKey) throw new Error("Durable upload origin key is invalid");
	return `poison:${originKey}:${durableIdentityKey(identity)}`;
}
